"use client";

import { cn } from "@v1/ui/utils";
import {
  type ImgHTMLAttributes,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { helpImageSource } from "./help-image-source";

/** Screenshot framed like the docs; a skeleton holds its box until the file arrives. */
export function HelpImage({
  src,
  alt,
}: ImgHTMLAttributes<HTMLImageElement>): ReactElement {
  const { src: url, width, natural } = helpImageSource(
    typeof src === "string" ? src : "",
  );
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  // Cached files can finish before hydration, then onLoad never fires.
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setLoaded(true);
  }, []);

  const done = (): void => setLoaded(true);
  const skeleton = loaded ? null : (
    <span aria-hidden className="absolute inset-0 animate-pulse bg-muted" />
  );

  if (natural) {
    // Size hint known: the exact box is reserved, so nothing below jumps.
    return (
      <span
        className="relative my-5 block max-w-full overflow-hidden rounded-lg border border-primary/15"
        style={{
          width: width ?? natural.width,
          aspectRatio: `${natural.width} / ${natural.height}`,
        }}
      >
        {skeleton}
        {/* Public SSR route: a plain <img> avoids next/image remote-host config. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imgRef}
          src={url}
          alt={alt ?? ""}
          loading="lazy"
          decoding="async"
          onLoad={done}
          onError={done}
          className={cn(
            "block h-full w-full transition-opacity duration-200",
            loaded ? "opacity-100" : "opacity-0",
          )}
        />
      </span>
    );
  }

  // Older images without a size hint: a column-wide placeholder until the real size is known.
  return (
    <span
      className={cn(
        "relative my-5 block max-w-full overflow-hidden rounded-lg border border-primary/15",
        loaded ? "w-fit" : "aspect-[16/10] w-full",
      )}
    >
      {skeleton}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        ref={imgRef}
        src={url}
        alt={alt ?? ""}
        loading="lazy"
        decoding="async"
        onLoad={done}
        onError={done}
        style={width ? { width } : undefined}
        className={cn(
          "block h-auto max-w-full",
          loaded ? "" : "absolute inset-0 opacity-0",
        )}
      />
    </span>
  );
}
